import { useState } from 'react';
import { useApp } from '../../AppProvider';
import { CalendarRow } from '../../settings/google/CalendarRow';
import { OutlookCalendarRow } from '../../settings/microsoft/OutlookCalendarRow';
import { useGoogleAccount } from '../../settings/google/useGoogleAccount';
import { useMicrosoftAccount } from '../../settings/microsoft/useMicrosoftAccount';
import { useCalendarSharingStore } from '../../../lib/stores/calendarSharingStore';
import { useSettingsStore } from '../../../lib/stores/settingsStore';
import { externalKey, setExternalLevel, toggleBusyCalendar } from '../../../lib/calendar/profile/externalVisibility';
import { ExternalCalendarsTable, type ExternalCalendarRow } from './ExternalCalendarsTable';
import { usePermissions } from '../../../lib/hooks/usePermissions';

export function LinkedCalendarsTab({ userId, organizationId }: { userId?: string; organizationId?: string }) {
  const { toast } = useApp();
  // Inviti a collegare Google/Outlook solo a chi ha il permesso "Collega Google o Outlook" (calendar.sync)
  const canSyncCalendar = usePermissions().hasPermission('calendar', 'sync');
  const google = useGoogleAccount(userId, organizationId);
  const ms = useMicrosoftAccount(userId);
  const { prefs, setPrefs } = useCalendarSharingStore();
  const max = (useSettingsStore(s => s.settings) as any)?.calendar_settings?.external?.max_visibility;
  const [open, setOpen] = useState<string[]>([]);
  const toggle = (k: string) => setOpen(o => o.includes(k) ? o.filter(x => x !== k) : [...o, k]);

  const save = (fn: (u: any) => Promise<boolean | undefined>) => async (u: any) => {
    const ok = await fn(u);
    if (ok) toast.success('Salvato'); else toast.error('Errore nel salvataggio');
    return !!ok;
  };

  const g = google.accountInfo;
  const m = ms.accountInfo;
  const rows: ExternalCalendarRow[] = [
    ...(g.connected ? google.googleCalendars.map(c => ({
      key: externalKey('google', c.id), name: c.summary, provider: 'Google' as const,
      seen: g.google_calendar_ids.includes(c.id), receivesTasks: g.sync_activities_calendar_id === c.id,
    })) : []),
    ...(m.connected ? ms.calendars.map(c => ({
      key: externalKey('microsoft', c.id), name: c.name, provider: 'Outlook' as const,
      seen: m.microsoft_calendar_ids.includes(c.id), receivesTasks: m.microsoft_sync_activities_calendar_id === c.id,
    })) : []),
  ];
  const idOf = (r: ExternalCalendarRow) => r.key.split(':').slice(1).join(':');

  const onSeen = (r: ExternalCalendarRow, on: boolean) => {
    if (r.provider === 'Google') {
      const ids = on ? [...g.google_calendar_ids, idOf(r)] : g.google_calendar_ids.filter(i => i !== idOf(r));
      void save(google.updateUserProfile)({ google_calendar_ids: ids });
    } else {
      const ids = on ? [...m.microsoft_calendar_ids, idOf(r)] : m.microsoft_calendar_ids.filter(i => i !== idOf(r));
      void save(ms.updateUserProfile)({ microsoft_calendar_ids: ids });
    }
  };
  const onReceives = (r: ExternalCalendarRow) => {
    if (r.provider === 'Google') void save(google.updateUserProfile)({ sync_activities_calendar_id: idOf(r) });
    else void save(ms.updateUserProfile)({ microsoft_sync_activities_calendar_id: idOf(r) });
  };
  const syncOff = (g.connected && !g.sync_activities) || (m.connected && !m.microsoft_sync_activities);

  return (
    <div className="space-y-4">
      {!g.connected && !m.connected && (
        <p className="text-[13px] text-[#787774]">
          {canSyncCalendar
            ? 'Nessun calendario collegato. Collega Google da "Accesso Google" qui sopra, oppure Outlook da Impostazioni, Microsoft.'
            : 'Nessun calendario esterno collegato: le attività restano nel calendario di Flusia.'}
        </p>
      )}
      {(g.connected || m.connected) && (
        <div className="border-y border-[rgba(55,53,47,0.09)] dark:border-white/10">
          {g.connected && (
            <CalendarRow active connected expanded={open.includes('g')} accountInfo={g} googleCalendars={google.googleCalendars}
              fetchingCalendars={google.fetchingCalendars} onToggle={() => toggle('g')} onActivate={() => toggle('g')} onSave={save(google.updateUserProfile)} />
          )}
          {m.connected && (
            <OutlookCalendarRow active connected expanded={open.includes('m')} accountInfo={m} calendars={ms.calendars}
              fetchingCalendars={ms.fetchingCalendars} onToggle={() => toggle('m')} onActivate={() => toggle('m')} onSave={save(ms.updateUserProfile)} />
          )}
        </div>
      )}
      {rows.length > 0 && (
        <ExternalCalendarsTable
          rows={rows}
          max={max}
          visibility={prefs.external_visibility}
          busy={prefs.busy_calendars || []}
          syncOffNote={syncOff}
          onSeen={onSeen}
          onReceives={onReceives}
          onBusy={(r, on) => void setPrefs({ busy_calendars: toggleBusyCalendar(prefs.busy_calendars, r.key, on) })}
          onLevel={(r, level) => void setPrefs({ external_visibility: setExternalLevel(prefs.external_visibility, r.key, level, max) })}
        />
      )}
      {(canSyncCalendar || g.connected || m.connected) && <p className="text-[11px] text-[#787774]">Per ora Flusia scrive sul tuo Google o Outlook, ma le modifiche fatte lì non tornano in Flusia: il ritorno arriva con la sincronizzazione a due vie, in preparazione.</p>}
    </div>
  );
}
